import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { loanProductQueryOptions } from '@/api/loans-api'
import type { LoanAccountDetail } from '@/api/loans-types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatMoney, type Pesewas } from '@/lib/money'

import { formatRate, INTEREST_PERIOD_LABELS, INTEREST_METHOD_LABELS } from './constants'
import { LoanExposureBanner } from './exposure-banner'
import { LoanLink } from './loan-link'
import { loanPaths, useLoanNavigate } from './loan-paths'
import {
  AmountField,
  AppliedOutcome,
  DateField,
  NoteField,
  NumberField,
  OperationForm,
} from './operation-form'
import { useLoanPermissions } from './permissions'
import { LoanPrerequisitesPanel } from './prerequisites-panel'
import { useApplyForRound } from './mutations'

/**
 * Applying for the next round on an account.
 *
 * The account is permanent; a round is one lending on it. So this screen belongs to the account
 * and produces a round — and the two figures it has to put in front of the officer before they
 * key anything are the gates and the growth cap. Both refuse an application after the fact, and
 * an officer who learns about them from a red banner has already told the member a number.
 */
export function ApplyForRoundForm({ account }: { account: LoanAccountDetail }) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const apply = useApplyForRound(account.id)

  const product = useQuery(loanProductQueryOptions(account.productId, permissions.canViewProducts))

  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [purpose, setPurpose] = useState('')
  const [instalments, setInstalments] = useState('')
  const [firstRepaymentDate, setFirstRepaymentDate] = useState('')
  const [createdCycleId, setCreatedCycleId] = useState<string | null>(null)

  const instalmentCount = Number.parseInt(instalments, 10)
  const trimmedPurpose = purpose.trim()
  const valid =
    amount !== null &&
    amount > 0 &&
    trimmedPurpose !== '' &&
    Number.isInteger(instalmentCount) &&
    instalmentCount > 0

  const lastCycle = [...account.cycles].sort((left, right) => right.cycleNumber - left.cycleNumber)[0]
  const openRound = account.cycles.find(
    (cycle) =>
      cycle.status !== 'Closed' &&
      cycle.status !== 'Rejected' &&
      cycle.status !== 'Withdrawn' &&
      cycle.status !== 'Cancelled' &&
      cycle.status !== 'WrittenOff',
  )

  return (
    <div className="space-y-4">
      <LoanExposureBanner customerId={account.customerId} />

      {openRound ? (
        <Alert tone="warning">
          <AlertTitle>A round is already open on this account</AlertTitle>
          <AlertDescription>
            Round {openRound.cycleNumber} ({openRound.displayReference}) is{' '}
            {openRound.status.toLowerCase()}. The server allows one open round at a time
            (<span className="font-mono text-xs">loans.cycle_already_open</span>), so this
            application will be refused until that one is closed, refused or called off.{' '}
            <LoanLink
              to={loanPaths.cycle(account.id, openRound.id)}
              className="font-medium text-brand-text underline underline-offset-2"
            >
              Open it
            </LoanLink>
            .
          </AlertDescription>
        </Alert>
      ) : null}

      {account.groupId ? (
        <Alert tone="info">
          <AlertTitle>This account lends through a group</AlertTitle>
          <AlertDescription>
            A further round is refused where the group&apos;s health does not qualify it
            (<span className="font-mono text-xs">loans.group_not_qualified</span>) and where health
            has never been computed (<span className="font-mono text-xs">loans.group_health_unknown</span>)
            — an unscored group has not qualified. The group screen carries the score, the inputs
            behind it, and the recompute.
          </AlertDescription>
        </Alert>
      ) : null}

      <OperationForm
        title="Apply for the next round"
        description="Captures the application. Nothing is committed until somebody appraises and approves it."
        submitLabel="Submit application"
        isPending={apply.isPending}
        disabled={!valid}
        error={apply.isError ? apply.error : null}
        onDone={() =>
          navigate(
            createdCycleId
              ? loanPaths.cycle(account.id, createdCycleId)
              : loanPaths.account(account.id),
          )
        }
        outcome={
          createdCycleId ? (
            <AppliedOutcome title="Application captured">
              The round is submitted and waiting to be appraised. Nothing has been promised to the
              member: the amount above is what was asked for, not what will be lent.
            </AppliedOutcome>
          ) : null
        }
        onSubmit={() => {
          if (!valid) {
            return
          }

          apply.mutate(
            {
              amountMinor: amount,
              purpose: trimmedPurpose,
              instalmentCount,
              requestedFirstRepaymentDate: firstRepaymentDate === '' ? null : firstRepaymentDate,
            },
            { onSuccess: (cycle) => setCreatedCycleId(cycle.summary.id) },
          )
        }}
        footnote="The principal is checked against the product's range and against the growth cap on the last round. Both are the server's, and both refuse rather than trim."
      >
        {product.isSuccess ? (
          <Alert tone="info">
            <AlertTitle>
              {product.data.name} — {INTEREST_METHOD_LABELS[product.data.interestMethod]} at{' '}
              {formatRate(product.data.interestRate)}{' '}
              {INTEREST_PERIOD_LABELS[product.data.interestRatePeriod]}
            </AlertTitle>
            <AlertDescription>
              Between {formatMoney(product.data.minPrincipalMinor)} and{' '}
              {formatMoney(product.data.maxPrincipalMinor)}, over{' '}
              {product.data.minInstalments}–{product.data.maxInstalments} instalments.
              {lastCycle ? (
                <>
                  {' '}
                  The last round was{' '}
                  <MoneyDisplay
                    value={lastCycle.principalMinor}
                    currency={lastCycle.currencyCode}
                    className="inline"
                  />
                  ; this product allows a further{' '}
                  {formatRate(product.data.maxRoundPrincipalGrowthPercent, 0)} on top of it.
                </>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <AmountField
            id="apply-amount"
            label="Amount applied for"
            value={amount}
            onChange={setAmount}
            autoFocus
            currency={lastCycle?.currencyCode}
            hint="What the member asked for, in their words and figures. The approved amount can be less."
          />

          <NumberField
            id="apply-instalments"
            label="Instalments"
            value={instalments}
            onChange={setInstalments}
            min={1}
            hint={
              product.isSuccess
                ? `Between ${product.data.minInstalments} and ${product.data.maxInstalments} on this product.`
                : 'Checked against the product range by the server.'
            }
          />
        </div>

        <NoteField
          id="apply-purpose"
          label="Purpose"
          required
          value={purpose}
          onChange={setPurpose}
          placeholder="Stock for the market stall"
          hint="What the money is for, as the member described it."
        />

        <DateField
          id="apply-first-repayment"
          label="Requested first repayment"
          required={false}
          value={firstRepaymentDate}
          onChange={setFirstRepaymentDate}
          hint="Optional. Left empty, the server derives it from the disbursement date and the product's frequency."
        />

        <LoanPrerequisitesPanel
          customerId={account.customerId}
          productId={account.productId}
          principalMinor={amount ?? undefined}
          title="Gates at this amount"
        />
      </OperationForm>
    </div>
  )
}
